import React from "react";
import { motion } from "framer-motion";
import { Mail, MapPin } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SITE, NAV_LINKS } from "../../lib/config";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isBuilders = location.pathname.startsWith("/builders");
  const isSubpage = location.pathname !== "/";

  const goHomeAnchor = (href) => (e) => {
    e.preventDefault();
    if (isSubpage) navigate("/" + href);
    else {
      const el = document.getElementById(href.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const footerCtaClick = (e) => {
    e.preventDefault();
    if (isBuilders) navigate("/#offer-form");
    else {
      document
        .getElementById("offer-form")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      id="contact"
      className="relative"
      style={{ background: "#122B20", color: "#FAF7F2" }}
      data-testid="site-footer"
    >
      {/* Contact strip */}
      <div
        className="border-b"
        style={{ borderColor: "rgba(198,161,91,0.25)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14"
          >
            <div>
              <p className="eyebrow">Prefer to talk?</p>
              <h3
                className="font-heading font-medium mt-3 max-w-md"
                style={{ fontSize: "clamp(1.5rem, 2.4vw, 2rem)", lineHeight: 1.2 }}
              >
                Prefer to talk to a <span className="gold-italic">person?</span>
              </h3>
              <p
                className="mt-4 font-body"
                style={{
                  color: "rgba(250,247,242,0.75)",
                  fontSize: "0.95rem",
                  lineHeight: 1.7,
                }}
              >
                {SITE.serviceArea}
              </p>
            </div>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <Mail
                  size={20}
                  color="#C6A15B"
                  strokeWidth={1.5}
                  className="mt-1"
                />
                <div>
                  <p
                    className="eyebrow"
                    style={{ color: "rgba(250,247,242,0.55)" }}
                  >
                    Email
                  </p>
                  <a
                    href={`mailto:${SITE.leadEmail}`}
                    className="block font-body mt-1 hover:text-[#C6A15B] transition-colors"
                    style={{ color: "#FAF7F2", fontSize: "1rem", wordBreak: "break-word" }}
                    data-testid="footer-email-brandon"
                  >
                    {SITE.leadEmail}
                  </a>
                  <a
                    href={`mailto:${SITE.secondaryEmail}`}
                    className="block font-body mt-1 hover:text-[#C6A15B] transition-colors"
                    style={{ color: "rgba(250,247,242,0.85)", fontSize: "1rem", wordBreak: "break-word" }}
                    data-testid="footer-email-katana"
                  >
                    {SITE.secondaryEmail}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin
                  size={20}
                  color="#C6A15B"
                  strokeWidth={1.5}
                  className="mt-1"
                />
                <div>
                  <p
                    className="eyebrow"
                    style={{ color: "rgba(250,247,242,0.55)" }}
                  >
                    Service Area
                  </p>
                  <p
                    className="font-body mt-1"
                    style={{ color: "#FAF7F2", fontSize: "1rem" }}
                  >
                    Nationwide · Home base: Denver, CO
                  </p>
                </div>
              </div>
            </div>

            <div className="flex md:justify-end">
              <a
                href="#offer-form"
                onClick={footerCtaClick}
                className="btn-primary self-start"
                data-testid="footer-cta"
              >
                Get My Cash Offer
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div
              className="inline-flex items-center rounded-2xl px-6 py-4"
              style={{
                background: "#FAF7F2",
                border: "1px solid rgba(198,161,91,0.35)",
                boxShadow:
                  "0 20px 40px -24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.4)",
              }}
              data-testid="footer-logo-plaque"
            >
              <img
                src={SITE.logoFooterUrl}
                alt="Ibex Acquisitions & Development"
                className="h-14 md:h-16 w-auto"
                loading="lazy"
              />
            </div>
            <p
              className="mt-5"
              style={{
                color: "#C6A15B",
                fontSize: "0.68rem",
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              Invest · Build · Elevate · Expand
            </p>
            <p
              className="mt-5 font-body max-w-sm"
              style={{
                color: "rgba(250,247,242,0.65)",
                fontSize: "0.9rem",
                lineHeight: 1.7,
              }}
            >
              Family-owned land acquisition and development. Straight talk, fair
              numbers, follow-through.
            </p>
          </div>

          <div className="md:col-span-4">
            <p
              className="eyebrow"
              style={{ color: "rgba(250,247,242,0.55)" }}
            >
              Navigate
            </p>
            <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3">
              {NAV_LINKS.map((l) => {
                if (l.to) {
                  return (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="font-body text-sm hover:text-[#C6A15B] transition-colors"
                        style={{ color: "rgba(250,247,242,0.8)" }}
                        data-testid={`footer-nav-${l.to.replace("/", "")}`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  );
                }
                return (
                  <li key={l.href}>
                    <a
                      href={isSubpage ? "/" + l.href : l.href}
                      onClick={goHomeAnchor(l.href)}
                      className="font-body text-sm hover:text-[#C6A15B] transition-colors"
                      style={{ color: "rgba(250,247,242,0.8)" }}
                      data-testid={`footer-nav-${l.href.replace("#", "")}`}
                    >
                      {l.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p
              className="eyebrow"
              style={{ color: "rgba(250,247,242,0.55)" }}
            >
              Legal
            </p>
            <p
              className="mt-5 font-body text-sm"
              style={{ color: "rgba(250,247,242,0.8)", lineHeight: 1.7 }}
            >
              © 2026 Ibex Acquisitions &amp; Development, operated by Vaulti
              LLC. All rights reserved.
            </p>
          </div>
        </div>

        <div
          className="mt-10 pt-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-start"
          style={{ borderTop: "1px solid rgba(198,161,91,0.2)" }}
        >
          <p
            className="font-body"
            style={{
              color: "rgba(168,162,154,0.85)",
              fontSize: "0.78rem",
              lineHeight: 1.7,
            }}
          >
            Ibex Acquisitions &amp; Development is a real estate investment and
            development company operated by Vaulti LLC. We are not licensed
            real estate brokers or agents, and we do not list properties or
            represent sellers. We purchase property directly for our own
            portfolio, projects, and development partners. Any offer made is
            non-binding, subject to due diligence, title review, and a fully
            executed written purchase agreement, and does not guarantee that a
            transaction will close. Estimated timelines are typical but not
            guaranteed.
          </p>
          <p
            className="font-body"
            style={{
              color: "rgba(168,162,154,0.7)",
              fontSize: "0.72rem",
              lineHeight: 1.65,
            }}
            data-testid="footer-image-disclaimer"
          >
            Home and property images shown throughout this site — including
            hero photography, galleries, and before/after transformations — are
            illustrative examples and renderings of potential outcomes. They do
            not depict specific parcels or projects and are not a guarantee of
            results, finishes, or future value.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
